import React, { useState } from 'react';
import {
  FaInstagram,
  FaFacebook,
  FaTiktok,
  FaMoneyBillWave,
  FaCreditCard,
  FaExchangeAlt,
  FaReceipt,
} from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

const menuLinks = [
  { to: '/#hero', label: 'Inicio' },
  { to: '/#nosotros', label: 'Nosotros' },
  { to: '/destinos', label: 'Destinos' },
  // { to: '/cruceros', label: 'Cruceros' }, // Ocultado temporalmente
  { to: '/asistencia', label: 'Asistencia' }, // si preferís el hash, usá '/#asistencia'
  { to: '/#contacto', label: 'Contacto' },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const location = useLocation();

  const handleClick = (to) => {
    const [pathname, hash] = to.split('#');

    // si cambia la ruta, redirigimos (el hook global se encarga del scroll al hash)
    if (pathname !== location.pathname) {
      window.location.href = to;
      return;
    }

    // misma ruta: si hay hash, scrolleo suave
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (to !== location.pathname) {
      window.history.pushState({}, '', to);
    }
  };

  const clearStatus = () => {
    setTimeout(() => {
      setStatus(null);
      setMessage('');
    }, 5000);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      setStatus('error');
      setMessage('El correo es obligatorio.');
      clearStatus();
      return;
    }

    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Ingresá un correo válido.');
      clearStatus();
      return;
    }

    try {
      setIsSending(true);
      const response = await fetch(
        'https://script.google.com/macros/s/AKfycbxcSWPYWCy90nm8l_GpFYDvRhKyuuTx05eIvoLEbwp6X0dm5WcLDWGjq6XIFgrLdikG/exec',
        { method: 'POST', body: new URLSearchParams({ email }) }
      );
      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || 'Ocurrió un error. Intentá nuevamente.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Ocurrió un error de red. Intentá nuevamente.');
    } finally {
      setIsSending(false);
      clearStatus();
    }
  };

  return (
    <footer className="bg-[#fffaf3] text-[#1C1C1C] pt-10 pb-6 px-4 text-sm border-t border-[#e7e4dc]">
      <div className="max-w-screen-xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 text-center md:text-left items-start">
        {/* Newsletter */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Suscribite al newsletter</h3>
          <p className="mb-3">Recibí promociones y novedades directamente en tu correo.</p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Tu email"
              required
              className="p-2 border border-gray-300 rounded w-full"
            />
            <button
              type="submit"
              disabled={isSending}
              className={`bg-[#B49A56] text-white px-4 py-2 rounded transition-colors ${
                isSending ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#9c8448]'
              }`}
            >
              {isSending ? 'Enviando...' : 'Suscribirme'}
            </button>
          </form>

          {message && (
            <div
              className={`mt-3 text-sm p-2 rounded ${
                status === 'success' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
              }`}
            >
              {message}
            </div>
          )}
        </div>

        {/* Menú (igual al Header) */}
        <div className="text-center md:text-center">
          <h3 className="text-lg font-semibold mb-3">Menú</h3>
          <ul className="space-y-2">
            {menuLinks.map((link) => (
              <li key={link.to}>
                <button
                  onClick={() => handleClick(link.to)}
                  className="hover:text-[#B49A56] transition-colors"
                >
                  {link.label}
                </button>
              </li>
            ))}
            <li>
              <button
                onClick={() => handleClick('/terminos-condiciones')}
                className="hover:text-[#B49A56] transition-colors"
              >
                Términos y condiciones
              </button>
            </li>
          </ul>

          {/* redes (ya que es el menú, sumo atajos abajo) */}
          <div className="flex justify-center gap-4 text-lg mt-4 text-[#B49A56]">
            <a href="https://facebook.com/belutours" target="_blank" rel="noopener noreferrer" title="Facebook">
              <FaFacebook />
            </a>
            <a href="https://instagram.com/belutours" target="_blank" rel="noopener noreferrer" title="Instagram">
              <FaInstagram />
            </a>
            <a href="https://www.tiktok.com/@belu.tours" target="_blank" rel="noopener noreferrer" title="TikTok">
              <FaTiktok />
            </a>
          </div>
        </div>

        {/* Eleginos / Formas de pago */}
        <div className="text-center md:text-right">
          <h3 className="text-lg font-semibold mb-3">Formas de pago</h3>
          <p className="mb-4">
            Contamos con diversas <span className="font-bold">formas de pago</span> para que planifiques tu viaje con tranquilidad y respaldo. Con Belu Tours, pagar tu viaje es tan simple como disfrutarlo.
          </p>
          <div className="flex justify-center md:justify-end gap-6 text-2xl text-[#B49A56]">
            <div className="flex flex-col items-center">
              <FaMoneyBillWave title="Efectivo" />
              <span className="text-xs text-gray-600 mt-1">Efectivo</span>
            </div>
            <div className="flex flex-col items-center">
              <FaExchangeAlt title="Transferencia" />
              <span className="text-xs text-gray-600 mt-1">Transferencia</span>
            </div>
            <div className="flex flex-col items-center">
              <FaReceipt title="Pagos parciales" />
              <span className="text-xs text-gray-600 mt-1">Pagos parciales</span>
            </div>
            <div className="flex flex-col items-center">
              <FaCreditCard title="Tarjetas de crédito" />
              <span className="text-xs text-gray-600 mt-1">Tarjetas</span>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-gray-500 mt-10 border-t border-[#e7e4dc] pt-4 space-y-2">
        <p>Copyright Belu Tours - Todos los derechos reservados.</p>
        <p>Creado con ❤️ <strong>NAB - Servicios Digitales</strong></p>
        <p>&copy; {currentYear} Belu Tours</p>
      </div>
    </footer>
  );
}
